/**
 * workspace/stubs.ts — CLI workspace tool stubs.
 *
 * Implements the 11 council workspace tools (`createPlan`, `createPhase`,
 * `createTask`, `updateTask`, `addIdea`, `createMilestone`,
 * `createDocument`, `searchDocuments`, `linkDocuments`,
 * `getDocumentBacklinks`) as filesystem-backed stubs that persist to
 * `.zelari/` at the project root.
 *
 * Replaces the AnathemaBrain Electron-only ctx (e.g. `ctx.addIdea(...)`)
 * with a `WorkspaceContext` that writes Markdown + YAML frontmatter files.
 *
 * @see docs/plans/2026-07-01-council-workspace-cli-stubs.md
 */

import {
  existsSync,
  readdirSync,
  writeFileSync,
  readFileSync,
  mkdirSync,
  renameSync,
} from "node:fs";
import { join, basename, dirname, relative } from "node:path";
import type { EnhancedToolDefinition } from "@zelari/core/types";
import { workspaceMutex } from "./storage.js";
import {
  resolveWorkspaceRoot,
  workspaceFile,
  workspaceArtifact,
  projectName as deriveProjectName,
} from "./paths.js";
import { Storage } from "./storage.js";
import type {
  WorkspaceContext,
  PlanFrontmatter,
  AdrFrontmatter,
  RiskFrontmatter,
  ReviewFrontmatter,
  DocFrontmatter,
} from "./types.js";

// ── Context factory ───────────────────────────────────────────────────

/**
 * Build a WorkspaceContext rooted at the project's `.zelari/` directory.
 * Use this in the CLI's `runCouncilPure` ctx slot.
 */
export function createWorkspaceContext(
  projectRoot: string = process.cwd(),
): WorkspaceContext {
  const rootDir = resolveWorkspaceRoot(projectRoot);
  return {
    rootDir,
    projectRoot,
    storage: new Storage(),
  };
}

// ── Plan (plan.md) helpers ────────────────────────────────────────────

interface PlanSummary {
  phases: PlanFrontmatter[];
  tasks: PlanFrontmatter[];
  milestones: PlanFrontmatter[];
  /**
   * v1.43 (ADR-0018): unknown plan.json root fields (schemaVersion, counter,
   * future extensions) preserved in pass-through across the
   * readPlan → writePlan cycle. Canonical fields below always win on write.
   */
  root?: Record<string, unknown>;
}

/** Path of the machine-readable plan store (source of truth since v0.7.3). */
function planJsonPath(ctx: WorkspaceContext): string {
  return join(ctx.rootDir, "plan.json");
}

function readPlan(ctx: WorkspaceContext): PlanSummary {
  // v0.7.3: the plan's source of truth is plan.json. The previous
  // implementation round-tripped the whole summary through the handwritten
  // YAML-subset frontmatter of plan.md; free-text names/descriptions with
  // colons, commas, and apostrophes corrupted the flow-map parse, so a phase
  // written by createPhase could be unreadable one call later ("Phase not
  // found" right after "Phase created", live test 2026-07-02). plan.md is
  // now only the human-readable rendering.
  const jsonPath = planJsonPath(ctx);
  if (existsSync(jsonPath)) {
    try {
      const parsed = JSON.parse(
        readFileSync(jsonPath, "utf8"),
      ) as Partial<PlanSummary> & Record<string, unknown>;
      // ADR-0018: keep unknown root fields (schemaVersion/counter/…) so the
      // next writePlan round-trips them for the task_* tool store.
      const { phases, tasks, milestones, ...root } = parsed;
      return {
        phases: Array.isArray(phases) ? phases : [],
        tasks: Array.isArray(tasks) ? tasks : [],
        milestones: Array.isArray(milestones) ? milestones : [],
        root,
      };
    } catch {
      // Corrupt plan.json — fall through to the legacy migration read.
    }
  }
  // Legacy migration: one best-effort parse of the old plan.md frontmatter
  // (pre-v0.7.3 files). The next writePlan persists it as plan.json.
  const path = workspaceFile(ctx.rootDir, "plan");
  const doc = ctx.storage.readIfExists<PlanFrontmatter>(path);
  if (!doc) return { phases: [], tasks: [], milestones: [] };
  const meta = doc.meta as PlanFrontmatter & {
    phases?: PlanFrontmatter[];
    tasks?: PlanFrontmatter[];
    milestones?: PlanFrontmatter[];
  };
  return {
    phases: Array.isArray(meta.phases) ? meta.phases : [],
    tasks: Array.isArray(meta.tasks) ? meta.tasks : [],
    milestones: Array.isArray(meta.milestones) ? meta.milestones : [],
  };
}

/**
 * Write the entire plan (phases + tasks + milestones):
 *   - plan.json — lossless machine-readable source of truth (atomic write).
 *   - plan.md   — human-readable rendering only (no machine-parsed arrays in
 *                 the frontmatter, so the fragile YAML round-trip is gone).
 */
function writePlan(ctx: WorkspaceContext, summary: PlanSummary): void {
  const jsonPath = planJsonPath(ctx);
  mkdirSync(dirname(jsonPath), { recursive: true });
  const tmp = jsonPath + ".tmp-" + process.pid;
  // ADR-0018: spread pass-through root fields first; canonical arrays win.
  writeFileSync(
    tmp,
    JSON.stringify(
      {
        ...(summary.root ?? {}),
        phases: summary.phases,
        tasks: summary.tasks,
        milestones: summary.milestones,
      },
      null,
      2,
    ),
    "utf8",
  );
  renameSync(tmp, jsonPath);

  const mdPath = workspaceFile(ctx.rootDir, "plan");
  const summaryMeta = {
    kind: "plan-summary",
    id: "_plan",
    updatedAt: new Date().toISOString(),
  };
  ctx.storage.write(mdPath, summaryMeta, renderPlanBody(summary));
}

/** Render the plan.md body in human-readable Markdown. */
function renderPlanBody(summary: PlanSummary): string {
  const lines: string[] = [];
  lines.push(`# Execution Plan`);
  lines.push("");
  lines.push(`> Auto-generated by Zelari Code council. Do not edit by hand.`);
  lines.push("");

  // Phases
  lines.push(`## Phases`);
  lines.push("");
  if (summary.phases.length === 0) {
    lines.push(`_(none yet)_`);
  } else {
    for (const phase of [...summary.phases].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    )) {
      lines.push(
        `### ${phase.order ?? "?"}. ${phase.id} \`${phase.color ?? ""}\``,
      );
      lines.push("");
    }
  }
  lines.push("");

  // Tasks grouped by phase
  lines.push(`## Tasks`);
  lines.push("");
  if (summary.tasks.length === 0) {
    lines.push(`_(none yet)_`);
  } else {
    const byPhase = new Map<string, PlanFrontmatter[]>();
    for (const task of summary.tasks) {
      const pid = task.phaseId ?? "_unassigned";
      if (!byPhase.has(pid)) byPhase.set(pid, []);
      byPhase.get(pid)!.push(task);
    }
    for (const [phaseId, tasks] of byPhase) {
      lines.push(`### Phase: \`${phaseId}\``);
      lines.push("");
      for (const t of tasks) {
        const status = t.status ?? "pending";
        const pri = t.priority ?? "medium";
        lines.push(`- [ ] **${t.id}** _(${status}, ${pri})_`);
      }
      lines.push("");
    }
  }
  lines.push("");

  // Milestones
  lines.push(`## Milestones`);
  lines.push("");
  if (summary.milestones.length === 0) {
    lines.push(`_(none yet)_`);
  } else {
    for (const m of summary.milestones) {
      lines.push(`- **${m.id}** → ${m.targetVersion ?? "?"}`);
    }
  }
  lines.push("");

  return lines.join("\n");
}

// ── ADR (decisions/) helpers ──────────────────────────────────────────

function nextAdrId(ctx: WorkspaceContext): string {
  const decisionsDir = join(ctx.rootDir, "decisions");
  if (!existsSync(decisionsDir)) return "001";
  const existing = readdirSync(decisionsDir)
    .filter((f) => f.endsWith(".md"))
    .map((f) => f.match(/^(\d+)-/))
    .filter((m): m is RegExpMatchArray => !!m)
    .map((m) => parseInt(m[1]!, 10));
  const max = existing.length === 0 ? 0 : Math.max(...existing);
  return String(max + 1).padStart(3, "0");
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
}

// ── Plan record helpers ───────────────────────────────────────────────
//
// Shared by the itemized stubs (createPhase / createTask / createMilestone)
// and the batch createPlan stub (v0.7.8) so both paths produce identical
// records and per-artifact files. Each helper mutates the in-memory summary
// only — the caller decides when to persist via writePlan (once per tool
// call, so the batch path does a single atomic plan.json write).

interface TaskInput {
  title: string;
  description: string;
  fileRefs: string[];
  acceptance: string[];
  qaScenario: string;
  priority: "low" | "medium" | "high" | "critical";
}

/** Add a phase record. Returns the phase id and whether it was newly created. */
function addPhaseRecord(
  summary: PlanSummary,
  input: { name: string; description: string; order: number; color: string },
): { id: string; created: boolean } {
  const id = slugify(input.name) || `phase-${input.order}`;
  if (summary.phases.some((p) => p.id === id)) {
    return { id, created: false };
  }
  summary.phases.push({
    kind: "phase",
    id,
    name: input.name,
    description: input.description,
    order: input.order,
    color: input.color,
  });
  return { id, created: true };
}

/**
 * Add a task record + its per-task file. With `dedupe: true` a task whose
 * title already exists in the same phase is treated as the same task and
 * NOT re-added (protects the createPlan batch path from duplicating tasks
 * that a partial itemized run already persisted). The itemized createTask
 * stub keeps `dedupe: false` for backward compatibility.
 */
function addTaskRecord(
  ctx: WorkspaceContext,
  summary: PlanSummary,
  phaseId: string,
  t: TaskInput,
  options: { dedupe: boolean },
): { id: string; created: boolean } {
  const slug = slugify(t.title);
  if (options.dedupe) {
    const existing = summary.tasks.find(
      (k) =>
        k.phaseId === phaseId &&
        k.id.replace(/-\d+$/, "") === `${phaseId}-${slug}`,
    );
    if (existing) return { id: existing.id, created: false };
  }
  const id = `${phaseId}-${slug}-${summary.tasks.filter((k) => k.phaseId === phaseId).length + 1}`;
  summary.tasks.push({
    kind: "task",
    id,
    // v0.7.3: keep the human-readable title in plan.json so
    // buildPlanSummary can render it in the system prompt.
    name: t.title,
    phaseId,
    status: "pending",
    priority: t.priority,
  });
  const taskPath = join(ctx.rootDir, "plan-tasks", `${id}.md`);
  const meta: PlanFrontmatter = {
    kind: "task",
    id,
    phaseId,
    status: "pending",
    priority: t.priority,
    tags: t.fileRefs,
  };
  const body = [
    `# ${t.title}`,
    "",
    t.description,
    "",
    `## File references`,
    ...t.fileRefs.map((f) => `- \`${f}\``),
    "",
    `## Acceptance criteria`,
    ...t.acceptance.map((a) => `- ${a}`),
    "",
    `## QA scenario`,
    "",
    t.qaScenario || "_(not specified)_",
    "",
  ].join("\n");
  ctx.storage.write(taskPath, meta, body);
  return { id, created: true };
}

/** Add a milestone record + its file. Returns id and whether newly created. */
function addMilestoneRecord(
  ctx: WorkspaceContext,
  summary: PlanSummary,
  input: {
    title: string;
    description: string;
    dueDate?: string;
    targetVersion?: string;
  },
): { id: string; created: boolean } {
  const id = `m-${slugify(input.title)}`;
  if (summary.milestones.some((m) => m.id === id)) {
    return { id, created: false };
  }
  const version = input.targetVersion ?? input.dueDate ?? "TBD";
  summary.milestones.push({
    kind: "milestone",
    id,
    name: input.title,
    description: input.description,
    dueDate: input.dueDate,
    targetVersion: version,
  });
  const path = join(ctx.rootDir, "milestones", `${id}.md`);
  const meta: PlanFrontmatter = {
    kind: "milestone",
    id,
    name: input.title,
    description: input.description,
    dueDate: input.dueDate,
    targetVersion: version,
  };
  const body = [
    `# Milestone: ${input.title}`,
    "",
    input.description,
    "",
    `Target version: ${version}`,
    "",
  ].join("\n");
  ctx.storage.write(path, meta, body);
  return { id, created: true };
}

/**
 * Read-only view of the current plan (phases + tasks + milestones).
 * Exported for the built-in complete-design fallback (completeDesign.ts)
 * so it can inspect per-phase task coverage without duplicating the
 * plan.json / legacy plan.md read logic.
 */
export function readPlanSummary(ctx: WorkspaceContext): {
  phases: PlanFrontmatter[];
  tasks: PlanFrontmatter[];
  milestones: PlanFrontmatter[];
  /** ADR-0018 pass-through root fields (schemaVersion, counter, …). */
  root?: Record<string, unknown>;
} {
  return readPlan(ctx);
}

// ── Stub factory ─────────────────────────────────────────────────────

/**
 * Create all 9 workspace stubs bound to the given context.
 * Pass the returned array into `runCouncilPure`'s `config.tools` (via a
 * tool registry), or register them in the global tool registry when
 * running the CLI.
 */
export function createWorkspaceStubs(
  ctx: WorkspaceContext,
): EnhancedToolDefinition[] {
  return [
    createPlanStub(ctx),
    createPhaseStub(ctx),
    createTaskStub(ctx),
    updateTaskStub(ctx),
    addIdeaStub(ctx),
    createMilestoneStub(ctx),
    createDocumentStub(ctx),
    createNfrSpecStub(ctx),
    searchDocumentsStub(ctx),
    linkDocumentsStub(ctx),
    getDocumentBacklinksStub(ctx),
  ];
}

// ── Individual stubs ──────────────────────────────────────────────────

/**
 * v0.7.8 — Batch plan creation. One tool call persists the WHOLE plan:
 * every phase, the tasks nested under each phase, and the milestone.
 *
 * Rationale: the itemized contract (4×createPhase + 12×createTask +
 * 1×createMilestone = 17 sequential tool calls) exceeds what mid-tier
 * council models reliably emit in one turn (composer-2.5 persisted ≤7 of
 * 17 in the 2026-07-03 live tests, see HANDOFF.md). A single structured
 * call collapses the whole contract into one emission, which the same
 * models satisfy reliably (proven by the 1-call retry paths of Minosse
 * and Lucifero).
 */
function createPlanStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: "createPlan",
    description:
      "Create the FULL project plan in one call: all phases, the tasks nested under each phase, and the target milestone. " +
      "Preferred over itemized createPhase/createTask/createMilestone calls — one createPlan call persists everything atomically.",
    category: "project",
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:plan`, () => {
        const rawPhases = Array.isArray(args["phases"])
          ? (args["phases"] as Record<string, unknown>[])
          : [];
        if (rawPhases.length === 0) {
          return 'createPlan requires a non-empty "phases" array (each phase: { name, description, order, color, tasks: [{ title, description, fileRefs, acceptance, qaScenario, priority }] }).';
        }
        const summary = readPlan(ctx);
        let phasesCreated = 0;
        let tasksCreated = 0;
        const phaseIds: string[] = [];
        rawPhases.forEach((rawPhase, idx) => {
          const { id: phaseId, created } = addPhaseRecord(summary, {
            name: (rawPhase["name"] as string) ?? `Phase ${idx + 1}`,
            description: (rawPhase["description"] as string) ?? "",
            order: (rawPhase["order"] as number) ?? idx + 1,
            color: (rawPhase["color"] as string) ?? "#3b82f6",
          });
          if (created) phasesCreated += 1;
          phaseIds.push(phaseId);
          const rawTasks = Array.isArray(rawPhase["tasks"])
            ? (rawPhase["tasks"] as Record<string, unknown>[])
            : [];
          for (const rawTask of rawTasks) {
            const { created: taskCreated } = addTaskRecord(
              ctx,
              summary,
              phaseId,
              {
                title: (rawTask["title"] as string) ?? "New Task",
                description: (rawTask["description"] as string) ?? "",
                fileRefs:
                  (rawTask["fileRefs"] as string[]) ??
                  (rawTask["files"] as string[]) ??
                  [],
                acceptance: (rawTask["acceptance"] as string[]) ?? [],
                qaScenario:
                  (rawTask["qaScenario"] as string) ??
                  (rawTask["qa"] as string) ??
                  "",
                priority: ((rawTask["priority"] as string) ??
                  "medium") as TaskInput["priority"],
              },
              { dedupe: true },
            );
            if (taskCreated) tasksCreated += 1;
          }
        });
        let milestonesCreated = 0;
        const rawMilestone = args["milestone"] as
          Record<string, unknown> | undefined;
        if (rawMilestone && typeof rawMilestone === "object") {
          const { created } = addMilestoneRecord(ctx, summary, {
            title:
              (rawMilestone["title"] as string) ?? "v0.1.0 design-complete",
            description: (rawMilestone["description"] as string) ?? "",
            dueDate: rawMilestone["dueDate"] as string | undefined,
            targetVersion: rawMilestone["targetVersion"] as string | undefined,
          });
          if (created) milestonesCreated += 1;
        }
        writePlan(ctx, summary);
        return `Plan created: ${phasesCreated} phases, ${tasksCreated} tasks, ${milestonesCreated} milestone(s). Phase ids: ${phaseIds.join(", ")}.`;
      });
    },
  };
}

function createPhaseStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: "createPhase",
    description:
      "Create a new execution phase in the project plan. Each phase is a milestone toward shipping a feature.",
    category: "project",
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:plan`, () => {
        const name = (args["name"] as string) ?? "New Phase";
        const order = (args["order"] as number) ?? 0;
        const summary = readPlan(ctx);
        const { id, created } = addPhaseRecord(summary, {
          name,
          description: (args["description"] as string) ?? "",
          order,
          color: (args["color"] as string) ?? "#3b82f6",
        });
        if (!created) {
          return `Phase "${id}" already exists.`;
        }
        writePlan(ctx, summary);
        return `Phase "${name}" created (id: ${id}, order: ${order}).`;
      });
    },
  };
}

function createTaskStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: "createTask",
    description:
      "Create a task within an existing phase. Tasks have file references, acceptance criteria, and a QA scenario.",
    category: "project",
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:plan`, () => {
        const phaseId =
          (args["phaseId"] as string) ?? (args["phase"] as string);
        const title = (args["title"] as string) ?? "New Task";
        if (!phaseId) return "Task requires a phaseId.";
        const summary = readPlan(ctx);
        if (!summary.phases.some((p) => p.id === phaseId)) {
          return `Phase "${phaseId}" not found. Create it first via /createPhase.`;
        }
        const { id } = addTaskRecord(
          ctx,
          summary,
          phaseId,
          {
            title,
            description: (args["description"] as string) ?? "",
            fileRefs:
              (args["fileRefs"] as string[]) ??
              (args["files"] as string[]) ??
              [],
            acceptance: (args["acceptance"] as string[]) ?? [],
            qaScenario:
              (args["qaScenario"] as string) ?? (args["qa"] as string) ?? "",
            priority: ((args["priority"] as string) ??
              "medium") as TaskInput["priority"],
          },
          { dedupe: false },
        );
        writePlan(ctx, summary);
        return `Task "${title}" created (id: ${id}) under phase "${phaseId}".`;
      });
    },
  };
}

function updateTaskStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: "updateTask",
    description: "Update a task's status (pending|in_progress|done|blocked).",
    category: "project",
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:plan`, () => {
        const taskId = args["taskId"] as string;
        const rawStatus = args["status"] as string;
        if (!taskId || !rawStatus)
          return "updateTask requires taskId and status.";
        const valid = ["pending", "in_progress", "done", "blocked"];
        // v0.7.3: models routinely use kanban vocabulary ("todo", "completed")
        // — map the common synonyms instead of erroring on each attempt.
        const aliases: Record<string, string> = {
          todo: "pending",
          "to-do": "pending",
          open: "pending",
          backlog: "pending",
          "in-progress": "in_progress",
          "in progress": "in_progress",
          doing: "in_progress",
          started: "in_progress",
          active: "in_progress",
          complete: "done",
          completed: "done",
          finished: "done",
          closed: "done",
          resolved: "done",
          stuck: "blocked",
          waiting: "blocked",
          "on-hold": "blocked",
          "on hold": "blocked",
        };
        const status = valid.includes(rawStatus)
          ? rawStatus
          : aliases[rawStatus.toLowerCase()];
        if (!status)
          return `Invalid status: ${rawStatus}. Must be one of: ${valid.join(", ")}.`;
        const summary = readPlan(ctx);
        const task = summary.tasks.find((t) => t.id === taskId);
        if (!task) return `Task "${taskId}" not found.`;
        task.status = status as "pending" | "in_progress" | "done" | "blocked";
        writePlan(ctx, summary);
        return `Task "${taskId}" updated to status="${status}".`;
      });
    },
  };
}

function addIdeaStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: "addIdea",
    description:
      "Add an Architecture Decision Record (ADR) to the project. ADRs document the context, decision, and consequences of significant choices.",
    category: "project",
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:adr`, () => {
        const title = (args["title"] as string) ?? "New Decision";
        const context =
          (args["context"] as string) ?? (args["content"] as string) ?? "";
        const decision = (args["decision"] as string) ?? title;
        const consequences = (args["consequences"] as string[]) ?? [];
        const tags = (args["tags"] as string[]) ?? [];
        const category = (args["category"] as string) ?? "General";
        const id = `${nextAdrId(ctx)}-${slugify(title)}`;
        const path = workspaceArtifact(ctx.rootDir, "decisions", id);
        const meta: AdrFrontmatter = {
          kind: "adr",
          status: "proposed",
          id,
          date: new Date().toISOString().slice(0, 10),
          tags,
        };
        const body = [
          `# ADR ${id}: ${title}`,
          "",
          `> Category: ${category}  ·  Status: **proposed**  ·  ${meta.date}`,
          "",
          `## Context`,
          "",
          context,
          "",
          `## Decision`,
          "",
          decision,
          "",
          `## Consequences`,
          "",
          ...consequences.map((c) => `- ${c}`),
          "",
        ].join("\n");
        ctx.storage.write(path, meta, body);
        return `ADR ${id} created: "${title}". Status: proposed. Promote to accepted via /update ADR or manual edit.`;
      });
    },
  };
}

function createMilestoneStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: "createMilestone",
    description:
      "Create a project milestone (a code version that ships when specific acceptance criteria are met).",
    category: "project",
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:plan`, () => {
        const title = (args["title"] as string) ?? "New Milestone";
        const summary = readPlan(ctx);
        const { id, created } = addMilestoneRecord(ctx, summary, {
          title,
          description: (args["description"] as string) ?? "",
          dueDate: args["dueDate"] as string | undefined,
          targetVersion: args["targetVersion"] as string | undefined,
        });
        if (!created) return `Milestone "${id}" already exists.`;
        writePlan(ctx, summary);
        const version =
          summary.milestones.find((m) => m.id === id)?.targetVersion ?? "TBD";
        return `Milestone "${title}" created (id: ${id}, version: ${version}).`;
      });
    },
  };
}

function createNfrSpecStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: "createNfrSpec",
    description:
      "Persist machine-readable NFR constraints for post-council verification (.zelari/nfr-spec.json). " +
      "Emit when the plan sets motion, JS budget, or performance guardrails.",
    category: "project",
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:nfr-spec`, () => {
        const targets = Array.isArray(args["targets"])
          ? (args["targets"] as string[])
          : ["index.html"];
        const spec = {
          version: 1 as const,
          targets,
          animation: {
            compositorOnly: args["compositorOnly"] !== false,
            forbidLayoutProps: args["forbidLayoutProps"] !== false,
          },
          inlineJs: {
            maxBytes:
              typeof args["inlineJsMaxBytes"] === "number"
                ? args["inlineJsMaxBytes"]
                : 5120,
          },
          planFeatureKeywords: Array.isArray(args["planFeatureKeywords"])
            ? (args["planFeatureKeywords"] as string[])
            : undefined,
        };
        const outPath = join(ctx.rootDir, "nfr-spec.json");
        writeFileSync(outPath, JSON.stringify(spec, null, 2), "utf8");
        return `NFR spec written to nfr-spec.json (${targets.length} target(s)).`;
      });
    },
  };
}

function createDocumentStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: "createDocument",
    description:
      "Create a Markdown document in the project docs (README/CHANGELOG/spec draft produced by the council).",
    category: "vault",
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:doc`, () => {
        const title = (args["title"] as string) ?? "New Document";
        const content = (args["content"] as string) ?? "";
        const tags = (args["tags"] as string[]) ?? [];
        // v0.7.8: models routinely pass the FILENAME as title ("risks.md",
        // "synthesis.md"), which slugified to "risks-md"/"synthesis-md" and
        // produced duplicated artifacts (docs/risks-md.md next to risks.md,
        // HANDOFF 2026-07-03 §5.1). Strip the extension before slugifying.
        const normalizedTitle = title.replace(/\.(md|markdown)\s*$/i, "");
        const slug = slugify(normalizedTitle) || `doc-${Date.now()}`;

        if (slug === "risks") {
          const risksPath = workspaceFile(ctx.rootDir, "risks");
          const riskMeta: RiskFrontmatter = {
            kind: "risk",
            id: "risks",
            severity: "medium",
            date: new Date().toISOString().slice(0, 10),
          };
          ctx.storage.write(risksPath, riskMeta, content);
          return `Document "${title}" created at risks.md (workspace root).`;
        }

        const path = workspaceArtifact(ctx.rootDir, "docs", slug);
        const meta: DocFrontmatter = {
          kind: "doc",
          id: slug,
          date: new Date().toISOString().slice(0, 10),
          tags,
        };
        ctx.storage.write(path, meta, content);
        return `Document "${title}" created at docs/${slug}.md.`;
      });
    },
  };
}

/**
 * After this many searchDocuments calls in one council run, every further
 * result carries a nudge to stop searching and act. Live test 2026-07-02:
 * one member burned ~25 near-identical searches (each phrased slightly
 * differently, so the duplicate-call cache never tripped) before doing any
 * actual work.
 */
const SEARCH_NUDGE_THRESHOLD = 5;

function searchDocumentsStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  let callCount = 0;
  return {
    name: "searchDocuments",
    description:
      "Search all workspace documents (`.zelari/**`) for a keyword. Returns matching files with snippets. " +
      "Search AT MOST 2-3 times, then act on the results: read the matched files with read_file instead of rephrasing the same search.",
    category: "analysis",
    parameters: [],
    execute: async (args) => {
      callCount++;
      const nudge =
        callCount > SEARCH_NUDGE_THRESHOLD
          ? `[note] This is searchDocuments call #${callCount} in this run — STOP searching. Read the files you already found with read_file and act on them.\n\n`
          : "";
      const rawQuery = ((args["query"] as string) ?? "").trim();
      const limit = (args["limit"] as number) ?? 5;
      if (!rawQuery) return "searchDocuments requires a query.";
      // v0.7.3: models routinely write boolean-ish queries ("plan OR phase OR
      // milestone"). The previous implementation substring-matched the WHOLE
      // query string, so every OR query — and any multi-word query that didn't
      // appear verbatim — returned "No matches" even for documents created
      // seconds earlier. Match any OR-phrase first, then fall back to
      // any-word matching per file.
      const phrases = rawQuery
        .toLowerCase()
        .split(/\s+or\s+/i)
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
      const words = rawQuery
        .toLowerCase()
        .split(/[^a-z0-9_-]+/)
        .filter(
          (w) => w.length >= 3 && w !== "or" && w !== "and" && w !== "the",
        );
      const files = [
        ...ctx.storage.listMarkdown(join(ctx.rootDir, "decisions")),
        ...ctx.storage.listMarkdown(join(ctx.rootDir, "docs")),
        ...ctx.storage.listMarkdown(join(ctx.rootDir, "reviews")),
        ...ctx.storage.listMarkdown(join(ctx.rootDir, "plan-tasks")),
        ...ctx.storage.listMarkdown(join(ctx.rootDir, "milestones")),
        workspaceFile(ctx.rootDir, "plan"),
        workspaceFile(ctx.rootDir, "risks"),
      ];
      const results: { path: string; snippet: string }[] = [];
      for (const file of files) {
        if (!existsSync(file)) continue;
        const raw = readFileSync(file, "utf8");
        const content = raw.toLowerCase();
        let idx = -1;
        let matchLen = 0;
        for (const p of phrases) {
          const i = content.indexOf(p);
          if (i >= 0) {
            idx = i;
            matchLen = p.length;
            break;
          }
        }
        if (idx < 0) {
          for (const w of words) {
            const i = content.indexOf(w);
            if (i >= 0) {
              idx = i;
              matchLen = w.length;
              break;
            }
          }
        }
        if (idx < 0) continue;
        const start = Math.max(0, idx - 40);
        const end = Math.min(raw.length, idx + matchLen + 40);
        const snippet =
          "…" + raw.slice(start, end).replace(/\n/g, " ").trim() + "…";
        results.push({
          path: relative(ctx.rootDir, file) || basename(file),
          snippet,
        });
        if (results.length >= limit) break;
      }
      if (results.length === 0)
        return `${nudge}No matches for "${rawQuery}" in workspace.`;
      return (
        nudge + results.map((r) => `[${r.path}]\n${r.snippet}`).join("\n\n")
      );
    },
  };
}

function linkDocumentsStub(ctx: WorkspaceContext): EnhancedToolDefinition {
  return {
    name: "linkDocuments",
    description:
      "Link two workspace artifacts. Adds the `related:` field to the source frontmatter so backreferences can be traced.",
    category: "vault",
    parameters: [],
    execute: async (args) => {
      return workspaceMutex.run(`${ctx.rootDir}:link`, () => {
        // v0.7.8: the provider-facing JSON schema (toolSchemas.ts) advertises
        // sourceId/targetPathOrTitle — accept those aliases too, otherwise
        // every schema-conformant call fails with "requires fromId and toId".
        const fromId =
          (args["fromId"] as string) ?? (args["sourceId"] as string);
        const toId =
          (args["toId"] as string) ??
          (args["targetId"] as string) ??
          (args["targetPathOrTitle"] as string);
        if (!fromId || !toId) return "linkDocuments requires fromId and toId.";
        // Find the source file by id
        const allFiles = [
          ...ctx.storage.listMarkdown(join(ctx.rootDir, "decisions")),
          ...ctx.storage.listMarkdown(join(ctx.rootDir, "docs")),
          ...ctx.storage.listMarkdown(join(ctx.rootDir, "reviews")),
        ];
        const source = allFiles.find((f) => {
          try {
            const meta = ctx.storage.read<{ id: string }>(f).meta;
            return meta.id === fromId;
          } catch {
            return false;
          }
        });
        if (!source) return `Source "${fromId}" not found.`;
        const doc = ctx.storage.read<
          AdrFrontmatter | DocFrontmatter | ReviewFrontmatter
        >(source);
        const related = doc.meta.related ?? [];
        if (!related.includes(toId)) related.push(toId);
        doc.meta.related = related;
        ctx.storage.write(source, doc.meta, doc.body);
        return `Linked ${fromId} → ${toId}.`;
      });
    },
  };
}

function getDocumentBacklinksStub(
  ctx: WorkspaceContext,
): EnhancedToolDefinition {
  return {
    name: "getDocumentBacklinks",
    description:
      "List all workspace artifacts that link to the given artifact via their `related:` frontmatter.",
    category: "analysis",
    parameters: [],
    execute: async (args) => {
      // v0.7.8: the provider-facing JSON schema advertises `id` — accept it.
      const targetId = (args["targetId"] as string) ?? (args["id"] as string);
      if (!targetId) return "getDocumentBacklinks requires targetId.";
      const allFiles = [
        ...ctx.storage.listMarkdown(join(ctx.rootDir, "decisions")),
        ...ctx.storage.listMarkdown(join(ctx.rootDir, "docs")),
        ...ctx.storage.listMarkdown(join(ctx.rootDir, "reviews")),
      ];
      const backlinks: string[] = [];
      for (const file of allFiles) {
        try {
          const doc = ctx.storage.read<{ id: string; related?: string[] }>(
            file,
          );
          if (doc.meta.id === targetId) continue;
          if ((doc.meta.related ?? []).includes(targetId)) {
            backlinks.push(doc.meta.id);
          }
        } catch {
          /* skip */
        }
      }
      if (backlinks.length === 0) return `No backlinks to "${targetId}".`;
      return backlinks.map((id) => `- ${id}`).join("\n");
    },
  };
}

/** Convenience: get the workspace root used by the stubs. */
export { resolveWorkspaceRoot, deriveProjectName };
